import { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Send,
  History,
  CheckCircle,
  XCircle,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  ExternalLink,
  User,
  Building2,
  Mail,
  MapPin,
} from 'lucide-react';
import { submitAvalaraRequest, getAvalaraHistory } from '../../services/avalaraApi';

// ─── HELPERS ────────────────────────────────────────────────────────────────

const formatDate = (iso) => {
  if (!iso) return '—';
  return new Date(iso).toLocaleString('en-US', {
    month: 'short', day: 'numeric', year: 'numeric',
    hour: 'numeric', minute: '2-digit', hour12: true,
  });
};

const EMPTY_FORM = {
  accountName: '',
  firstName: '',
  lastName: '',
  email: '',
  addressLine: '',
  region: '',
  country: 'US',
  postalCode: '',
};

// ─── SUCCESS CARD ────────────────────────────────────────────────────────────

const SuccessCard = ({ record, onNewRequest }) => (
  <div className="max-w-xl mx-auto mt-6">
    <div className="rounded-2xl border border-green-200 dark:border-green-800 bg-green-50 dark:bg-green-950/40 p-6 space-y-4">
      <div className="flex items-center gap-3">
        <CheckCircle className="text-green-500 shrink-0" size={28} />
        <div>
          <p className="font-semibold text-green-800 dark:text-green-300 text-lg">Account Created Successfully</p>
          <p className="text-sm text-green-700 dark:text-green-400">The Avalara account has been set up.</p>
        </div>
      </div>

      <div className="divide-y divide-green-200 dark:divide-green-800 rounded-xl overflow-hidden border border-green-200 dark:border-green-800 bg-white dark:bg-slate-900">
        <Row label="Account ID" value={record.avalaraAccountId ?? '—'} mono />
        <Row label="Emailed To" value={record.email} />
        <Row label="Account Name" value={record.accountName} />
        <Row label="Created" value={formatDate(record.createdAt)} />
        {record.paymentUrl && (
          <div className="flex items-center justify-between px-4 py-3">
            <span className="text-sm text-gray-500 dark:text-gray-400">Payment Setup</span>
            <a
              href={record.paymentUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-1 text-sm text-blue-600 dark:text-blue-400 hover:underline font-medium"
            >
              Add Payment Method <ExternalLink size={13} />
            </a>
          </div>
        )}
      </div>

      <button
        onClick={onNewRequest}
        className="w-full py-2.5 rounded-xl bg-green-600 hover:bg-green-700 text-white font-medium text-sm transition-colors"
      >
        Submit Another Request
      </button>
    </div>
  </div>
);

const Row = ({ label, value, mono = false }) => (
  <div className="flex items-center justify-between px-4 py-3">
    <span className="text-sm text-gray-500 dark:text-gray-400">{label}</span>
    <span className={`text-sm font-medium text-gray-900 dark:text-gray-100 ${mono ? 'font-mono' : ''}`}>
      {String(value)}
    </span>
  </div>
);

// ─── HISTORY ROW ─────────────────────────────────────────────────────────────

const HistoryRow = ({ record }) => {
  const [expanded, setExpanded] = useState(false);

  return (
    <>
      <tr
        className="hover:bg-gray-50 dark:hover:bg-slate-800 cursor-pointer transition-colors"
        onClick={() => setExpanded((p) => !p)}
      >
        <td className="px-4 py-3 text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
          {formatDate(record.createdAt)}
        </td>
        <td className="px-4 py-3 text-sm font-medium text-gray-900 dark:text-gray-100">
          {record.accountName}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
          {record.firstName} {record.lastName}
        </td>
        <td className="px-4 py-3 text-sm text-gray-600 dark:text-gray-300">
          {record.email}
        </td>
        <td className="px-4 py-3 text-sm font-mono text-gray-700 dark:text-gray-200">
          {record.avalaraAccountId ?? <span className="text-gray-400">—</span>}
        </td>
        <td className="px-4 py-3">
          {record.success ? (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-green-100 dark:bg-green-900 text-green-700 dark:text-green-300">
              <CheckCircle size={11} /> Success
            </span>
          ) : (
            <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-red-100 dark:bg-red-900 text-red-700 dark:text-red-300">
              <XCircle size={11} /> Failed
            </span>
          )}
        </td>
        <td className="px-4 py-3 text-gray-400 dark:text-gray-500">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </td>
      </tr>
      {expanded && (
        <tr className="bg-gray-50 dark:bg-slate-900">
          <td colSpan={7} className="px-6 py-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
              <div className="space-y-1">
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">Request Details</p>
                <Detail label="Address" value={record.addressLine} />
                <Detail label="Region" value={record.region} />
                <Detail label="Country" value={record.country} />
                <Detail label="Postal Code" value={record.postalCode} />
                <Detail label="Submitted By" value={record.createdBy} />
              </div>
              <div className="space-y-1">
                <p className="font-medium text-gray-700 dark:text-gray-300 mb-2">Response Details</p>
                <Detail label="Status Code" value={record.statusCode} mono />
                {record.paymentUrl && (
                  <div className="flex items-start gap-2">
                    <span className="text-gray-500 dark:text-gray-400 shrink-0">Payment URL</span>
                    <a
                      href={record.paymentUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-blue-600 dark:text-blue-400 hover:underline flex items-center gap-1"
                      onClick={(e) => e.stopPropagation()}
                    >
                      Open <ExternalLink size={11} />
                    </a>
                  </div>
                )}
                {record.errorMessage && (
                  <div>
                    <p className="text-gray-500 dark:text-gray-400 mb-1">Error</p>
                    <pre className="text-xs text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-950/30 rounded p-2 overflow-x-auto whitespace-pre-wrap break-all max-h-32">
                      {record.errorMessage}
                    </pre>
                  </div>
                )}
              </div>
            </div>
          </td>
        </tr>
      )}
    </>
  );
};

const Detail = ({ label, value, mono = false }) => (
  <div className="flex items-start gap-2">
    <span className="text-gray-500 dark:text-gray-400 shrink-0">{label}:</span>
    <span className={`text-gray-800 dark:text-gray-200 ${mono ? 'font-mono' : ''}`}>
      {value || <span className="text-gray-400">—</span>}
    </span>
  </div>
);

// ─── INPUT FIELD ─────────────────────────────────────────────────────────────

const Field = ({ label, required, children }) => (
  <div>
    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
      {label}{required && <span className="text-red-500 ml-0.5">*</span>}
    </label>
    {children}
  </div>
);

const inputCls =
  'w-full px-3 py-2 text-sm border border-gray-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-colors';

// ─── MAIN COMPONENT ──────────────────────────────────────────────────────────

const AvalaraLicensing = () => {
  const [tab, setTab] = useState('new');
  const [form, setForm] = useState(EMPTY_FORM);
  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [successRecord, setSuccessRecord] = useState(null);

  const [history, setHistory] = useState([]);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [historyError, setHistoryError] = useState(null);

  const loadHistory = useCallback(async () => {
    setHistoryLoading(true);
    setHistoryError(null);
    try {
      const data = await getAvalaraHistory();
      setHistory(data || []);
    } catch (err) {
      setHistoryError(err.message || 'Failed to load history.');
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  useEffect(() => {
    if (tab === 'history') loadHistory();
  }, [tab, loadHistory]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((p) => ({ ...p, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setSubmitError(null);
    try {
      const record = await submitAvalaraRequest({
        accountName: form.accountName.trim(),
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        addressLine: form.addressLine.trim() || null,
        region: form.region.trim() || null,
        country: form.country.trim() || null,
        postalCode: form.postalCode.trim() || null,
      });
      setSuccessRecord(record);
      setForm(EMPTY_FORM);
    } catch (err) {
      const details = err.details;
      setSubmitError(
        (details && (details.error || details.message)) ||
        err.message ||
        'An unexpected error occurred.'
      );
    } finally {
      setSubmitting(false);
    }
  };

  const handleNewRequest = () => {
    setSuccessRecord(null);
    setTab('new');
  };

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="w-10 h-10 rounded-xl bg-blue-100 dark:bg-blue-900 flex items-center justify-center">
          <FileText size={20} className="text-blue-600 dark:text-blue-400" />
        </div>
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Avalara Licensing</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400">Create new Avalara accounts and view request history</p>
        </div>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 p-1 bg-gray-100 dark:bg-slate-800 rounded-xl w-fit">
        {[
          { id: 'new', label: 'New Request', icon: Send },
          { id: 'history', label: 'History', icon: History },
        ].map(({ id, label, icon: Icon }) => (
          <button
            key={id}
            onClick={() => { setTab(id); setSuccessRecord(null); }}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              tab === id
                ? 'bg-white dark:bg-slate-700 text-blue-600 dark:text-blue-400 shadow-sm'
                : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-200'
            }`}
          >
            <Icon size={15} />
            {label}
          </button>
        ))}
      </div>

      {/* ── New Request Tab ── */}
      {tab === 'new' && (
        <>
          {successRecord ? (
            <SuccessCard record={successRecord} onNewRequest={handleNewRequest} />
          ) : (
            <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-700 p-6">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Account Info */}
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <Building2 size={16} className="text-blue-500" />
                    <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Account Information
                    </h2>
                  </div>
                  <Field label="Company / Account Name" required>
                    <input
                      type="text"
                      name="accountName"
                      value={form.accountName}
                      onChange={handleChange}
                      required
                      placeholder="e.g. Black Star Auctions"
                      className={inputCls}
                    />
                  </Field>
                </section>

                <hr className="border-gray-100 dark:border-slate-800" />

                {/* Contact Info */}
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <User size={16} className="text-blue-500" />
                    <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Contact Information
                    </h2>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Field label="First Name" required>
                      <input
                        type="text"
                        name="firstName"
                        value={form.firstName}
                        onChange={handleChange}
                        required
                        placeholder="First name"
                        className={inputCls}
                      />
                    </Field>
                    <Field label="Last Name" required>
                      <input
                        type="text"
                        name="lastName"
                        value={form.lastName}
                        onChange={handleChange}
                        required
                        placeholder="Last name"
                        className={inputCls}
                      />
                    </Field>
                  </div>
                  <div className="mt-4">
                    <div className="flex items-center gap-2 mb-1">
                      <Mail size={14} className="text-gray-400" />
                      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
                        Email <span className="text-red-500">*</span>
                      </label>
                    </div>
                    <input
                      type="email"
                      name="email"
                      value={form.email}
                      onChange={handleChange}
                      required
                      placeholder="contact@company.com"
                      className={inputCls}
                    />
                  </div>
                </section>

                <hr className="border-gray-100 dark:border-slate-800" />

                {/* Company Address */}
                <section>
                  <div className="flex items-center gap-2 mb-4">
                    <MapPin size={16} className="text-blue-500" />
                    <h2 className="text-sm font-semibold text-gray-700 dark:text-gray-300 uppercase tracking-wide">
                      Company Address
                    </h2>
                  </div>
                  <div className="space-y-4">
                    <Field label="Street Address">
                      <input
                        type="text"
                        name="addressLine"
                        value={form.addressLine}
                        onChange={handleChange}
                        placeholder="123 Main St, Suite 100"
                        className={inputCls}
                      />
                    </Field>
                    <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
                      <Field label="State / Region">
                        <input
                          type="text"
                          name="region"
                          value={form.region}
                          onChange={handleChange}
                          placeholder="IL"
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Country">
                        <input
                          type="text"
                          name="country"
                          value={form.country}
                          onChange={handleChange}
                          placeholder="US"
                          maxLength={10}
                          className={inputCls}
                        />
                      </Field>
                      <Field label="Postal Code">
                        <input
                          type="text"
                          name="postalCode"
                          value={form.postalCode}
                          onChange={handleChange}
                          placeholder="60611"
                          className={inputCls}
                        />
                      </Field>
                    </div>
                  </div>
                </section>

                {/* Error message */}
                {submitError && (
                  <div className="flex items-start gap-3 rounded-xl bg-red-50 dark:bg-red-950/30 border border-red-200 dark:border-red-800 px-4 py-3">
                    <XCircle size={18} className="text-red-500 shrink-0 mt-0.5" />
                    <p className="text-sm text-red-700 dark:text-red-400">{submitError}</p>
                  </div>
                )}

                {/* Submit */}
                <div className="flex justify-end pt-2">
                  <button
                    type="submit"
                    disabled={submitting}
                    className="flex items-center gap-2 px-6 py-2.5 rounded-xl bg-blue-600 hover:bg-blue-700 disabled:opacity-60 disabled:cursor-not-allowed text-white font-medium text-sm transition-colors"
                  >
                    {submitting ? (
                      <>
                        <RefreshCw size={15} className="animate-spin" />
                        Submitting…
                      </>
                    ) : (
                      <>
                        <Send size={15} />
                        Create Account
                      </>
                    )}
                  </button>
                </div>
              </form>
            </div>
          )}
        </>
      )}

      {/* ── History Tab ── */}
      {tab === 'history' && (
        <div className="bg-white dark:bg-slate-900 rounded-2xl border border-gray-200 dark:border-slate-700 overflow-hidden">
          {/* Toolbar */}
          <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 dark:border-slate-800">
            <p className="text-sm text-gray-500 dark:text-gray-400">
              {historyLoading ? 'Loading…' : `${history.length} request${history.length !== 1 ? 's' : ''}`}
            </p>
            <button
              onClick={loadHistory}
              disabled={historyLoading}
              className="flex items-center gap-1.5 text-sm text-gray-500 dark:text-gray-400 hover:text-blue-600 dark:hover:text-blue-400 transition-colors"
            >
              <RefreshCw size={14} className={historyLoading ? 'animate-spin' : ''} />
              Refresh
            </button>
          </div>

          {historyError && (
            <div className="flex items-center gap-3 px-5 py-4 bg-red-50 dark:bg-red-950/30 border-b border-red-200 dark:border-red-800">
              <XCircle size={16} className="text-red-500 shrink-0" />
              <p className="text-sm text-red-700 dark:text-red-400">{historyError}</p>
            </div>
          )}

          {!historyLoading && !historyError && history.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-16 text-gray-400 dark:text-gray-500 gap-3">
              <History size={36} />
              <p className="text-sm">No requests yet. Submit one to see it here.</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm border-collapse">
                <thead>
                  <tr className="bg-gray-50 dark:bg-slate-950 border-b border-gray-200 dark:border-slate-700">
                    {['Date', 'Account Name', 'Contact', 'Email', 'Account ID', 'Status', ''].map((h) => (
                      <th
                        key={h}
                        className="px-4 py-3 text-left text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider"
                      >
                        {h}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100 dark:divide-slate-800">
                  {historyLoading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <tr key={i} className="animate-pulse">
                          {Array.from({ length: 7 }).map((__, j) => (
                            <td key={j} className="px-4 py-3">
                              <div className="h-4 bg-gray-200 dark:bg-slate-700 rounded w-3/4" />
                            </td>
                          ))}
                        </tr>
                      ))
                    : history.map((record) => (
                        <HistoryRow key={record.id} record={record} />
                      ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AvalaraLicensing;
